import { VideoDownloadPolicy } from "~/components/VideoPlayer"
import React from "react"
import { createRoot } from "react-dom/client"
import { App as AntdApp, ConfigProvider, message, Spin } from "antd"
import zhCN from "antd/locale/zh_CN"
import { buildAppTheme } from "~/common/theme"
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
} from "react-router"
import { AuthGuard, LoginModal, PointBalanceGuard } from "~/components"
import { ROUTER } from "~/common/router"
import { AppLayout } from "~/layouts/AppLayout"
import {
  emitUnauthorized,
  subscribeUnauthorized,
} from "~/common/auth/authEvents"
import { configureRequestHandlers } from "~/common/request"
import { initDebugConsole } from "~/common/debug"
import { Home } from "~/pages/Home"
import { XqtCallback } from "~/pages/XqtCallback"
import { ChannelCallback } from "~/pages/ChannelCallback"
import { useSessionStore } from "~/store/session"
import { useThemeStore } from "~/store/theme"
import { tracker } from "~/common/analytics"
import { captureEntrySpm } from "~/common/analytics/spm"
import { init as initSentry } from "@sentry/react"
import "antd/dist/reset.css"
import "./index.css"

initDebugConsole()

configureRequestHandlers({
  onError: (error) => {
    void message.error(error.message)
  },
  onUnauthorized: () => emitUnauthorized(),
})

initSentry({
  dsn: "https://108e1de90b8949c3852278913ae20c93@sentry.i.meideng.net/92",
  tracesSampleRate: 1.0,
})

captureEntrySpm()

// 首页之外的页面按路由懒加载，减小首屏包体积
const router = createBrowserRouter([
  {
    element: (
      <>
        <Outlet />
        <PointBalanceGuard />
      </>
    ),
    hydrateFallbackElement: <Spin fullscreen />,
    children: [
      {
        path: ROUTER.TEMPLATE_DETAIL,
        lazy: () =>
          import("~/pages/TemplateDetail").then((page) => ({
            Component: page.TemplateDetail,
          })),
      },
      {
        path: ROUTER.XQT_CALLBACK,
        element: <XqtCallback />,
      },
      {
        path: ROUTER.DOUDIAN_CALL_BACK,
        element: <ChannelCallback channel="doudian" />,
      },
      {
        path: ROUTER.DOUYIN_CALL_BACK,
        element: <ChannelCallback channel="douyin" />,
      },
      {
        path: ROUTER.HOME,
        element: (
          <VideoDownloadPolicy value={true}>
            <AppLayout />
          </VideoDownloadPolicy>
        ),
        children: [
          {
            index: true,
            element: <Home />,
          },
          {
            element: <AuthGuard />,
            children: [
              {
                path: ROUTER.TEMPLATE_FAVORITES,
                lazy: () =>
                  import("~/pages/TemplateFavorites").then((page) => ({
                    Component: page.TemplateFavorites,
                  })),
              },
              {
                path: ROUTER.ANALYSIS,
                lazy: () =>
                  import("~/pages/VideoAnalysis").then((page) => ({
                    Component: page.VideoAnalysis,
                  })),
              },
              {
                path: ROUTER.VIDEO_SUBTITLE_ERASURE,
                lazy: () =>
                  import("~/pages/VideoSubtitleErasure").then((page) => ({
                    Component: page.VideoSubtitleErasure,
                  })),
              },
              {
                path: ROUTER.VIDEO_ENHANCEMENT,
                lazy: () =>
                  import("~/pages/VideoEnhancement").then((page) => ({
                    Component: page.VideoEnhancement,
                  })),
              },
              {
                path: ROUTER.ONE_CLICK_COPY,
                lazy: () =>
                  import("~/pages/OneClickCopy").then((page) => ({
                    Component: page.OneClickCopy,
                  })),
              },
              {
                path: ROUTER.RECREATE,
                lazy: () =>
                  import("~/pages/VideoRecreate").then((page) => ({
                    Component: page.VideoRecreate,
                  })),
              },
              {
                path: ROUTER.RECREATE_TASK,
                lazy: () =>
                  import("~/pages/VideoRecreate").then((page) => ({
                    Component: page.VideoRecreate,
                  })),
              },
              {
                path: ROUTER.RECREATE_V2,
                lazy: () =>
                  import("~/pages/VideoRecreateV2").then((page) => ({
                    Component: page.VideoRecreateV2,
                  })),
              },
              {
                path: ROUTER.RECREATE_V2_TASK,
                lazy: () =>
                  import("~/pages/VideoRecreateV2").then((page) => ({
                    Component: page.VideoRecreateV2,
                  })),
              },
              {
                path: ROUTER.TASKS,
                lazy: () =>
                  import("~/pages/TaskList").then((page) => ({
                    Component: page.TaskList,
                  })),
              },
              {
                path: ROUTER.ORDERS,
                lazy: () =>
                  import("~/pages/OrderHistory").then((page) => ({
                    Component: page.OrderHistory,
                  })),
              },
              {
                path: "*",
                element: (
                  <Navigate
                    to={ROUTER.HOME}
                    replace
                  />
                ),
              },
            ],
          },
        ],
      },
    ],
  },
])

const root = document.getElementById("root")

if (!root) {
  throw new Error("Root element #root not found")
}

const App = () => {
  const initSession = useSessionStore((state) => state.init)
  const mode = useThemeStore((state) => state.mode)

  // html 也要跟随主题：原生控件按 color-scheme 切换，全局滚动条颜色按 data-theme 切换
  React.useEffect(() => {
    document.documentElement.style.colorScheme = mode
    document.documentElement.dataset.theme = mode
  }, [mode])

  React.useEffect(() => {
    void initSession().then(() => {
      void tracker.initializeWithSubscription(
        () => useSessionStore.getState().user?.id,
        (callback) =>
          useSessionStore.subscribe((state) => {
            callback(state.user?.id)
          }),
      )
    })
  }, [initSession])

  React.useEffect(() => {
    return subscribeUnauthorized(() => {
      useSessionStore.getState().clear()
      useSessionStore.getState().openLoginModal()
    })
  }, [])

  return (
    <ConfigProvider
      locale={zhCN}
      theme={buildAppTheme(mode)}
    >
      {/* Antd App provides the cssVar scope class for plain page styles. */}
      <AntdApp>
        <RouterProvider router={router} />
        <LoginModal />
      </AntdApp>
    </ConfigProvider>
  )
}

createRoot(root).render(<App />)
